<template>
  <div ref="root" class="relative inline-block">
    <button
      type="button"
      class="inline-flex items-center justify-between w-full gap-2 h-9 px-3 text-xs transition bg-white border rounded-lg dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-primary/40 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
      :class="open ? 'border-primary ring-2 ring-primary/40' : ''"
      :style="{ minWidth }"
      :aria-expanded="open"
      @click.stop="toggle"
    >
      <span class="truncate">{{ selectedLabel }}</span>
      <i
        class="mdi mdi-chevron-down text-[14px] text-slate-400 shrink-0 transition-transform"
        :class="open ? 'rotate-180 text-primary' : ''"
      ></i>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        class="fixed z-[90] overflow-hidden bg-white border shadow-lg rounded-xl dark:bg-slate-900 border-slate-200 dark:border-slate-700"
        :style="menuStyle"
        @click.stop
      >
        <ul
          class="py-1 max-h-60 overflow-y-auto"
          role="listbox"
        >
          <li v-for="opt in options" :key="String(opt.value)">
            <button
              type="button"
              role="option"
              class="flex items-center w-full px-3 py-2 text-xs text-start transition"
              :class="
                isSelected(opt.value)
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-slate-700 dark:text-slate-200 hover:bg-secondary/10 hover:text-secondary'
              "
              :aria-selected="isSelected(opt.value)"
              @click="select(opt.value)"
            >
              <span class="truncate">{{ opt.label }}</span>
            </button>
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";

const props = defineProps({
  modelValue: { type: [String, Number], default: "" },
  options: { type: Array, required: true },
  minWidth: { type: String, default: "8.5rem" },
  align: { type: String, default: "end" },
});

const emit = defineEmits(["update:modelValue"]);

const open = ref(false);
const root = ref(null);
const menuStyle = ref({});

const selectedLabel = computed(() => {
  const found = props.options.find(
    (o) => String(o.value) === String(props.modelValue),
  );
  return found?.label ?? "";
});

function isSelected(value) {
  return String(value) === String(props.modelValue);
}

function positionMenu() {
  const btn = root.value?.querySelector("button");
  if (!btn) return;

  const rect = btn.getBoundingClientRect();
  const minW = Math.max(rect.width, 120);
  const gap = 4;
  const edge = 8;
  const estimatedH = Math.min(props.options.length * 36 + 8, 240);

  let top = rect.bottom + gap;
  if (top + estimatedH > window.innerHeight - edge) {
    top = Math.max(edge, rect.top - estimatedH - gap);
  }

  let left;
  if (props.align === "start") {
    left = rect.left;
  } else {
    left = rect.right - minW;
  }
  left = Math.min(
    Math.max(edge, left),
    window.innerWidth - minW - edge,
  );

  menuStyle.value = {
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
    width: `${Math.round(minW)}px`,
  };
}

async function toggle() {
  if (open.value) {
    open.value = false;
    return;
  }
  open.value = true;
  await nextTick();
  positionMenu();
}

function select(value) {
  emit("update:modelValue", value);
  open.value = false;
}

function onDocClick(e) {
  if (root.value && !root.value.contains(e.target)) open.value = false;
}

function onKeydown(e) {
  if (e.key === "Escape") open.value = false;
}

function onReposition() {
  if (open.value) positionMenu();
}

onMounted(() => {
  document.addEventListener("click", onDocClick);
  document.addEventListener("keydown", onKeydown);
  window.addEventListener("resize", onReposition);
  window.addEventListener("scroll", onReposition, true);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocClick);
  document.removeEventListener("keydown", onKeydown);
  window.removeEventListener("resize", onReposition);
  window.removeEventListener("scroll", onReposition, true);
});
</script>
